/**
 * Reconnaissance de l'unité en tête d'une ligne d'ingrédient déjà amputée de sa
 * quantité (§3.2). Essaie les préfixes de 4 mots à 1 mot, du plus long au plus
 * court, pour reconnaître aussi bien une unité en un mot (« g », « gousses »)
 * qu'une unité en plusieurs mots (« c. à s. », « cuillères à soupe »).
 */

import type { Unit } from '@mlc/shared';
import { normalizeForMatch, UNIT_ALIASES } from './units';

export interface UnitMatch {
  unit: Unit;
  /**
   * Longueur, en caractères, du préfixe reconnu dans le texte d'origine —
   * virgule finale comprise. Renvoyer la longueur du préfixe *nettoyé*
   * laisserait la virgule en tête du reste, où le parseur la prendrait pour le
   * séparateur nom/note : l'ingrédient se retrouverait sans nom.
   */
  length: number;
}

const MAX_UNIT_WORDS = 4;

function prefixRegExp(wordCount: number): RegExp {
  return new RegExp(`^(?:\\S+\\s+){${wordCount - 1}}\\S+`);
}

export function matchLeadingUnit(text: string): UnitMatch | null {
  const wordCount = text.split(/\s+/).filter(Boolean).length;

  for (let n = Math.min(MAX_UNIT_WORDS, wordCount); n >= 1; n--) {
    const prefixMatch = prefixRegExp(n).exec(text);
    if (!prefixMatch) continue;

    // Une unité suivie d'une virgule (« gousses, » avant une note) doit rester
    // reconnaissable : on essaie le préfixe brut, puis sans sa virgule finale.
    const rawPrefix = prefixMatch[0];
    for (const prefix of [rawPrefix, rawPrefix.replace(/,$/, '')]) {
      const unit = UNIT_ALIASES.get(normalizeForMatch(prefix));
      if (unit) {
        return { unit, length: rawPrefix.length };
      }
    }
  }

  return null;
}
